import React, { useEffect, useState } from "react";
import { statistic2 } from "services/statistic/statistic2";
import styles from "./statistic.module.css";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { downloadFile, generateCSVContent, months } from "utils/statistic";
import { FacultyData, YearlyData } from "modules/statistic";
import Popup from "components/Popup/popup";
import ErrorFailed from "components/errorFailed/errorFailed";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import NoDataScreen from "components/noDataScreen/NoDataScreen";
import Spinner from "components/spinner/Spinner";

export default function Statistic2() {
  const [facultyData, setFacultyData] = useState<FacultyData>({});
  const [yearlyData, setYearlyData] = useState<YearlyData>({});
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await statistic2(startDate, endDate);
      setFacultyData(res.faculty_data);
      console.log(res.faculty_data)
      setYearlyData(res.yearly_data);
      const years = Object.keys(res.yearly_data).map(Number);

      const maxYear = Math.max(...years);
      const minYear = Math.min(...years);

      setMaxYear(maxYear);
      setMinYear(minYear);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    } finally {
      setLoading(false)
    }
  };

  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError("Bitte geben Sie sowohl ein Startdatum als auch ein Enddatum ein.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Das Startdatum kann nicht nach dem Enddatum liegen.");
      return;
    }

    setIsPopupOpen(false);
    setError(null);
    setIsPopupOpen(false);
    fetchData();
  };

  const handleDeleteFilter = () => {
    setStartDate('');
    setEndDate('');
    setError(null)
    setIsPopupOpen(false);
  };

  const downloadCSV = () => {
    const headers = [
      "Kategorie",
      "Monat",
      ...months,
      "Gesamt",
      "Kundengruppen anteil"
    ];

    const rows: (string | number)[][] = [];



    Object.entries(yearlyData).forEach(([year, types]) => {
      Object.entries(types).forEach(([type, data]) => {
        const row = [
          type,
          year,
          ...months.map((_, monthIndex) =>
            (data as { months: { [key: number]: number } }).months[monthIndex + 1] || 0
          ),
          (data as { total: number }).total,
          (data as { percentage: number }).percentage.toFixed(2) + "%"
        ];
        rows.push(row);
      });
    });

    Object.entries(facultyData).forEach(([faculty, years]) => {
      Object.entries(years).forEach(([year, data]) => {
        const row = [
          faculty,
          year,
          ...months.map((_, monthIndex) => data.months[monthIndex + 1] || 0),
          data.total,
          data.percentage + "%"
        ];
        rows.push(row);
      });
    });

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Statistik2.csv");
  };


  const downloadPDF = () => {
    if (!maxYear) return; // Falls maxYear noch nicht gesetzt

    const doc = new jsPDF("p", "pt", "a4"); // Querformat
    const pageWidth = doc.internal.pageSize.getWidth();

    // 1. Titel & Beschreibung
    const title = "Statistik 2";
    const description = startDate && endDate
      ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der monatlichen Buchungen nach Fakultäten.`
      : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der monatlichen Buchungen nach Fakultäten.`;

    doc.setFontSize(18);
    doc.text(title, pageWidth / 2, 30, { align: "center" });
    doc.setFontSize(12);
    doc.text(description, pageWidth / 2, 50, { align: "center", maxWidth: pageWidth - 40 });

    // 2. Tabelle vorbereiten
    const tableHeaders = [
      "Kategorie",
      "Monat",
      ...months,
      "Gesamt",
      "Kundengruppen anteil"
    ];

    const tableRows: (string | number)[][] = [];



    // YearlyData
    Object.entries(yearlyData)
      .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
      .forEach(([year, types]) => {
        Object.entries(types).forEach(([type, data]) => {
          const row: (string | number)[] = [
            type,
            year,
            ...months.map((_, monthIndex) => (data as any).months[monthIndex + 1] || 0),
            (data as any).total,
            `${(data as any).percentage.toFixed(2)}%`
          ];
          tableRows.push(row);
        });
      });
    tableRows.push(Array(tableHeaders.length).fill(""));

    // FacultyData
    Object.entries(facultyData)
      .forEach(([faculty, years]) => {
        Object.entries(years)
          .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
          .forEach(([year, data], yearIndex) => {
            const row: (string | number)[] = [
              yearIndex === 0 ? faculty : "",
              year,
              ...months.map((_, monthIndex) => data.months[monthIndex + 1] || 0),
              data.total,
              `${data.percentage.toFixed(2)}%`
            ];
            tableRows.push(row);
          });
        tableRows.push(Array(tableHeaders.length).fill(""));

      });

    // 3. AutoTable erstellen
    autoTable(doc, {
      startY: 70,
      head: [tableHeaders],
      body: tableRows,
      theme: "grid",
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
      bodyStyles: { halign: "center", valign: "middle", textColor: [51, 51, 51] },
      styles: { fontSize: 10, cellPadding: 2 },
      didParseCell: (data) => {
        const row = data.row.raw as (string | number)[];
        const isEmptyRow = row.every(cell => cell === ""); // تحقق إذا كان الصف فارغ
        if (isEmptyRow) {
          data.cell.styles.fillColor = [255, 255, 255]; // الأبيض دائماً
        } else if (data.section === 'body') {
          const year = Number(row[1]);
          if (!isNaN(year)) {
            // الصفوف العادية: زوجي = رمادي، فردي = أبيض
            data.cell.styles.fillColor = year % 2 === 0 ? [220, 220, 220] : [255, 255, 255];
          }
        }
      }
    });


    doc.save("Statistik2.pdf");
  };





  useEffect(() => {
    fetchData();
  }, [isPopupOpen]);

  const hasAnyValue = (obj: any) =>
    Object.values(obj).some((years: any) =>
      Object.values(years).some((year: any) =>
        Object.values(year.months ?? {}).some((v: unknown) => Number(v) > 0)
      )
    );

  const hasNoData =
    !hasAnyValue(facultyData) &&
    !hasAnyValue(yearlyData);

  return (
    loading ? <Spinner /> :
      hasNoData ? <NoDataScreen /> :
        <>
          <StatisticFormat
            label="Statistik2"
            description={startDate && endDate
              ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der monatlichen Buchungen nach Fakultäten.`
              : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der monatlichen Buchungen nach Fakultäten.`
            }
            onCsvButton1Click={downloadCSV}
            onPdfButton1Click={downloadPDF}
            onFilterButton1Click={() => setIsPopupOpen(true)}
          >
            <div className={styles.container}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Kategorie</th>
                    <th>Monat</th>
                    {months.map((month) => (
                      <th key={month}>{month}</th>
                    ))}
                    <th className={styles.gesamt}>Gesamt</th>
                    <th className={styles.kundengruppenanteil}>Kundengruppen anteil</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={styles.emptyRow}>
                    {Array.from({ length: 16 }).map((_, index) => (
                      <td key={`empty-cell-${index}`}></td>
                    ))}
                  </tr>
                  {Object.entries(yearlyData)
                    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                    .map(([year, types]) => {
                      return (
                        <React.Fragment key={year}>
                          {Object.entries(types).map(([type, data], typeIndex) => (
                            <React.Fragment key={`${year}-${type}`}>
                              <tr className={styles[`year-${(parseInt(year)) % 2}`]}>
                                <td>{type}</td>
                                <td>{year}</td>
                                {months.map((month, monthIndex) => (
                                  <td key={monthIndex}>
                                    {(data as { months: { [key: number]: number } }).months[monthIndex + 1] || 0}
                                  </td>
                                ))}
                                <td>{(data as { total: number }).total}</td>
                                <td>{(data as { percentage: number }).percentage.toFixed(2)}%</td>
                              </tr>
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      );
                    })}

                  <tr className={styles.emptyRow}>
                    {Array.from({ length: 16 }).map((_, index) => (
                      <td key={`empty-cell-${index}`}></td>
                    ))}
                  </tr>
                  {Object.entries(facultyData).map(([faculty, years], facultyIndex) => (
                    <React.Fragment key={faculty}>
                      {Object.entries(years)
                        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                        .map(([year, data], yearIndex) => {
                          return (
                            <React.Fragment key={`${faculty}-${year}`}>
                              <tr className={styles[`year-${(parseInt(year)) % 2}`]}>
                                <td>{yearIndex === 0 ? faculty : ''}</td>
                                <td>{year}</td>
                                {months.map((month, monthIndex) => (
                                  <td key={monthIndex}>
                                    {data.months[monthIndex + 1] || 0}
                                  </td>
                                ))}
                                <td>{data.total}</td>
                                <td>{data.percentage.toFixed(2)}%</td>
                              </tr>
                            </React.Fragment>
                          );
                        })}

                      <tr className={styles.emptyRow}>
                        {Array.from({ length: 16 }).map((_, index) => (
                          <td key={`empty-cell-${index}`}></td>
                        ))}
                      </tr>
                    </React.Fragment>
                  ))}

                </tbody>
              </table>
            </div>
          </StatisticFormat>
          <Popup
            title={'Filter'}
            submitLabel={"Einsetzen"}
            closeLabel={"Zurücksetzen"}
            isWithButton={true}
            isOpen={isPopupOpen}
            onClose={handleDeleteFilter} onSubmit={handleFilter}>
            {error && <ErrorFailed error={error} />}

            <div className={commonStyles.formGroupRow}>
              <div className={commonStyles.formGroup}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className={commonStyles.formGroup}>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </Popup></>
  );
}
